import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  People,
  AttachMoney,
  LocalShipping,
} from '@mui/icons-material';
import { fetchMRRAnalytics, formatCurrency } from '../../services/pipelineService';
import toast from 'react-hot-toast';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  loading?: boolean;
  onClick?: () => void;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  loading,
  onClick,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': onClick
          ? {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              borderColor: 'primary.main',
            }
          : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <Skeleton variant="text" width={120} height={40} />
            ) : (
              <Typography variant="h4" component="div" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {value}
              </Typography>
            )}
            {subtitle && !loading && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && !loading && (
              <Box display="flex" alignItems="center" mt={1}>
                <TrendingUp
                  sx={{
                    fontSize: 16,
                    color: trend.isPositive ? 'success.main' : 'error.main',
                    transform: trend.isPositive ? 'none' : 'rotate(180deg)',
                    mr: 0.5,
                  }}
                />
                <Typography
                  variant="caption"
                  color={trend.isPositive ? 'success.main' : 'error.main'}
                  fontWeight="medium"
                >
                  {trend.isPositive ? '+' : ''}
                  {trend.value}%
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.isValidElement(icon) 
              ? React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 32, color: 'inherit' } })
              : icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

interface PipelineKPICardsProps {
  onCardClick?: (cardType: 'mrr' | 'subscriptions' | 'avg-value' | 'leads') => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

const PipelineKPICards: React.FC<PipelineKPICardsProps> = ({
  onCardClick,
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds
}) => {
  const [loading, setLoading] = useState(true);
  const [mrrData, setMrrData] = useState({
    totalMrr: 0,
    activeSubscriptions: 0,
    avgSubscriptionValue: 0,
  });

  const loadMRRData = async () => {
    try {
      const data = await fetchMRRAnalytics();
      setMrrData({
        totalMrr: data.totalMrr,
        activeSubscriptions: data.activeSubscriptions,
        avgSubscriptionValue: data.avgSubscriptionValue,
      });
    } catch (error: any) {
      console.error('Failed to fetch MRR analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMRRData();

    if (autoRefresh) {
      const interval = setInterval(loadMRRData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          title="Total MRR"
          value={formatCurrency(mrrData.totalMrr)}
          subtitle="Monthly Recurring Revenue"
          icon={<AttachMoney />}
          loading={loading}
          onClick={() => onCardClick?.('mrr')}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          title="Active Subscriptions"
          value={mrrData.activeSubscriptions}
          subtitle="Current clients"
          icon={<People />}
          loading={loading}
          onClick={() => onCardClick?.('subscriptions')}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          title="Avg Subscription Value"
          value={formatCurrency(mrrData.avgSubscriptionValue)}
          subtitle="Per client per month"
          icon={<TrendingUp />}
          loading={loading}
          onClick={() => onCardClick?.('avg-value')}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          title="Leads This Week"
          value="-"
          subtitle="Coming soon"
          icon={<LocalShipping />}
          loading={loading}
          onClick={() => onCardClick?.('leads')}
        />
      </Grid>
    </Grid>
  );
};

export default PipelineKPICards;
